import ModalShell from './ModalShell';

function EyeIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

function statusLabel(row) {
  const s = String(row?.status || '').toLowerCase();
  if (s === 'sent') return { text: 'Sent', className: 'ds-body font-semibold text-green-700' };
  if (s === 'failed') return { text: 'Failed', className: 'ds-body font-semibold text-red-600' };
  return { text: row?.status || '—', className: 'ds-body text-[hsl(var(--ds-muted-fg))]' };
}

/** Status text + borderless eye to view sent email body. */
export function EmailAlertViewButton({ row, onView }) {
  const { text, className } = statusLabel(row);
  const canPreview =
    typeof onView === 'function' &&
    (String(row?.status || '').toLowerCase() === 'sent' ||
      String(row?.status || '').toLowerCase() === 'failed' ||
      row?.subject ||
      row?.bodyHtml ||
      row?.bodyText);

  return (
    <span className="inline-flex items-center gap-2 min-h-[1.75rem]">
      <span className={className}>{text}</span>
      {canPreview && (
        <button
          type="button"
          className="inline-flex items-center justify-center p-1 text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)]/80 rounded-sm cursor-pointer border-0 bg-transparent shadow-none appearance-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-primary))]"
          title="View email content"
          aria-label={`View email: ${row?.subject || 'message'}`}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onView(row);
          }}
        >
          <EyeIcon />
        </button>
      )}
    </span>
  );
}

export default function EmailAlertPreviewModal({ alert, onClose }) {
  if (!alert) return null;

  const toLabel = Array.isArray(alert.to) ? alert.to.join(', ') : alert.to || '—';
  const when = alert.createdAt ? new Date(alert.createdAt).toLocaleString() : '—';
  const bodyHtml = alert.bodyHtml || '';
  const bodyText = alert.bodyText || '';
  const hasHtml = Boolean(bodyHtml.trim());
  const hasText = Boolean(bodyText.trim());

  return (
    <ModalShell
      title={String(alert.status || '').toLowerCase() === 'failed' ? 'Failed email' : 'Sent email'}
      subtitle={alert.subject || 'No subject'}
      onClose={onClose}
      maxWidth="max-w-3xl"
      zIndex="z-[15000]"
      footer={
        <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={onClose}>
          Close
        </button>
      }
    >
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs mb-4 pb-4 border-b border-[hsl(var(--ds-border))]">
        <div>
          <dt className="text-[hsl(var(--ds-muted-fg))]">When</dt>
          <dd className="text-[hsl(var(--ds-fg))]">{when}</dd>
        </div>
        <div>
          <dt className="text-[hsl(var(--ds-muted-fg))]">Status</dt>
          <dd className={statusLabel(alert).className}>{statusLabel(alert).text}</dd>
        </div>
        <div>
          <dt className="text-[hsl(var(--ds-muted-fg))]">Event</dt>
          <dd className="text-[hsl(var(--ds-fg))]">{alert.eventType || '—'}</dd>
        </div>
        {alert.audience && (
          <div>
            <dt className="text-[hsl(var(--ds-muted-fg))]">Audience</dt>
            <dd className="text-[hsl(var(--ds-fg))]">{alert.audience}</dd>
          </div>
        )}
        <div className="sm:col-span-2">
          <dt className="text-[hsl(var(--ds-muted-fg))]">To</dt>
          <dd className="text-[hsl(var(--ds-fg))] break-all">{toLabel}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-[hsl(var(--ds-muted-fg))]">Subject</dt>
          <dd className="text-[hsl(var(--ds-fg))]">{alert.subject || '—'}</dd>
        </div>
        {String(alert.status || '').toLowerCase() === 'failed' && alert.error && (
          <div className="sm:col-span-2">
            <dt className="text-[hsl(var(--ds-muted-fg))]">Error</dt>
            <dd className="text-red-600">{alert.error}</dd>
          </div>
        )}
      </dl>

      <p className="text-xs font-medium text-[hsl(var(--ds-fg))] mb-2">Message body (as sent)</p>
      {hasHtml ? (
        <div
          className="border border-[hsl(var(--ds-border))] max-h-[min(55vh,28rem)] overflow-auto bg-[hsl(var(--ds-surface))] p-4 text-sm prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      ) : hasText ? (
        <pre className="text-xs whitespace-pre-wrap border border-[hsl(var(--ds-border))] p-4 bg-[hsl(var(--ds-muted))] max-h-[min(55vh,28rem)] overflow-auto font-sans text-[hsl(var(--ds-fg))]">
          {bodyText}
        </pre>
      ) : (
        <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-2">
          <p>Full message body was not stored for this log entry (common for older alerts).</p>
          <p className="text-xs text-amber-800">
            Recipients and subject above are from the delivery log. New emails store HTML/text here automatically.
          </p>
        </div>
      )}
    </ModalShell>
  );
}
